import React, { useState, useEffect } from "react";
import { Stack, Text, Icon, MessageBar, MessageBarType } from "@fluentui/react";
import ProviderConfiguration from "@/components/webhooks/ProviderConfiguration";
import WebhookProviderPanel from "@/components/webhooks/WebhookProviderPanel";
import EventProcessing from "@/components/webhooks/EventProcessing";
import {
  InboundWebhookProvider,
  createInboundWebhookProvider,
} from "@/types/webhooks";
import {
  getAllActiveInboundWebhookProviders,
  deleteInboundWebhookProvider,
} from "@/api/webhookProviders";

const WebhooksInboundPage = () => {
  const [isProviderViewActive, setIsProviderViewActive] = useState(false);
  const [isEventProcessingActive, setIsEventProcessingActive] = useState(false);
  const [isEventProcessingPolling, setIsEventProcessingPolling] =
    useState(false);
  const [dotCount, setDotCount] = useState(0);
  const [providerWidth, setProviderWidth] = useState(50); // Percentage width for provider panel

  // Real data from API
  const [providers, setProviders] = useState<InboundWebhookProvider[]>([]);
  const [isLoadingProviders, setIsLoadingProviders] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Provider panel state
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [panelMode, setPanelMode] = useState<"create" | "edit">("create");
  const [selectedProvider, setSelectedProvider] = useState<
    InboundWebhookProvider | undefined
  >(undefined);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [warningMessages, setWarningMessages] = useState<string[]>([]);

  // Load providers from API
  const loadProviders = async () => {
    setIsLoadingProviders(true);
    setError(null);
    try {
      const apiProviders = await getAllActiveInboundWebhookProviders();
      const domainProviders = apiProviders.map(createInboundWebhookProvider);
      setProviders(domainProviders);
    } catch (err) {
      setError("Failed to load webhook providers");
      console.error("Error loading providers:", err);
    } finally {
      setIsLoadingProviders(false);
    }
  };

  // Handle provider deletion
  const handleDeleteProvider = async (provider: InboundWebhookProvider) => {
    try {
      const success = await deleteInboundWebhookProvider(provider.Id);
      if (success) {
        setProviders(providers.filter((p) => p.Id !== provider.Id));
      } else {
        setError("Failed to delete provider");
      }
    } catch (err) {
      setError("Error deleting provider");
      console.error("Error deleting provider:", err);
    }
  };

  // Handle adding new provider
  const handleAddProvider = () => {
    setPanelMode("create");
    setSelectedProvider(undefined);
    setIsPanelOpen(true);
  };

  // Handle editing existing provider
  const handleEditProvider = (provider: InboundWebhookProvider) => {
    setPanelMode("edit");
    setSelectedProvider(provider);
    setIsPanelOpen(true);
  };

  // Handle provider saved (create or update)
  const handleProviderSaved = (savedProvider: any) => {
    if (panelMode === "create") {
      // Add new provider
      const domainProvider = createInboundWebhookProvider(savedProvider);
      setProviders([...providers, domainProvider]);
    } else if (panelMode === "edit" && selectedProvider) {
      // Update existing provider
      const updatedDomainProvider = createInboundWebhookProvider({
        Id: selectedProvider.Id,
        InboundWebhookSecurityTypeId:
          savedProvider.InboundWebhookSecurityTypeId,
        InboundWebhookProviderTypeId:
          selectedProvider.InboundWebhookProviderTypeId,
        Description: savedProvider.Description,
        ProviderUrl: savedProvider.ProviderUrl,
      });
      setProviders(
        providers.map((p) =>
          p.Id === selectedProvider.Id ? updatedDomainProvider : p
        )
      );
    }
  };

  // Handle success messages
  const handleShowSuccess = (message: string) => {
    setSuccessMessage(message);
    // Auto-clear success message after 5 seconds
    setTimeout(() => {
      setSuccessMessage(null);
    }, 5000);
  };

  // Handle warning messages
  const handleShowWarnings = (messages: string[]) => {
    setWarningMessages(messages);
    // Auto-clear warning messages after 8 seconds
    setTimeout(() => {
      setWarningMessages([]);
    }, 8000);
  };

  useEffect(() => {
    loadProviders();
  }, []);

  const handleProviderClick = () => {
    setIsProviderViewActive(true);
  };

  const handleCloseProvider = () => {
    setIsProviderViewActive(false);
  };

  const handleEventProcessingClick = () => {
    setIsEventProcessingActive(true);
  };

  const handleCloseEventProcessing = () => {
    setIsEventProcessingActive(false);
    setIsEventProcessingPolling(false);
  };

  const handleEventProcessingPollingChange = (isPolling: boolean) => {
    setIsEventProcessingPolling(isPolling);
    if (!isPolling) {
      setDotCount(0); // Reset dots when polling stops
    }
  };

  // Handle dot animation for Event Processing label
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isEventProcessingPolling && isEventProcessingActive) {
      interval = setInterval(() => {
        setDotCount((prev) => (prev + 1) % 4); // Cycle through 0, 1, 2, 3
      }, 500); // Change dots every 500ms
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isEventProcessingPolling, isEventProcessingActive]);

  const isAnySectionActive = isProviderViewActive || isEventProcessingActive;
  const bothSectionsActive = isProviderViewActive && isEventProcessingActive;

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = providerWidth;
    const containerRect =
      e.currentTarget.parentElement?.getBoundingClientRect();

    if (!containerRect) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - startX;
      const containerWidth = containerRect.width;
      const deltaPercent = (deltaX / containerWidth) * 100;
      const newWidth = Math.max(20, Math.min(80, startWidth + deltaPercent));
      setProviderWidth(newWidth);
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  return (
    <Stack styles={{ root: { height: "100%" } }}>
      {/* CSS Animation for fading */}
      <style>
        {`
          @keyframes eventProcessingFade {
            0% { opacity: 1; }
            50% { opacity: 0.3; }
            100% { opacity: 1; }
          }
          .event-processing-fade {
            animation: eventProcessingFade 2s ease-in-out infinite;
          }
        `}
      </style>
      {/* Top Section */}
      <Stack
        tokens={{ childrenGap: 32 }}
        styles={{
          root: {
            padding: isAnySectionActive ? "24px 24px" : "48px 24px",
            height: isAnySectionActive ? "15%" : "100%",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
          },
        }}
      >
        {/* Icon - Hidden when any section is active */}
        {!isAnySectionActive && (
          <Icon
            iconName="CloudDownload"
            styles={{
              root: {
                fontSize: 120,
                color: "#0078d4",
                opacity: 0.8,
              },
            }}
          />
        )}

        {/* Main Title - Hidden when any section is active */}
        {!isAnySectionActive && (
          <Text
            variant="xxLarge"
            styles={{
              root: {
                fontWeight: "600",
                color: "#323130",
                marginBottom: "16px",
              },
            }}
          >
            Inbound Webhooks
          </Text>
        )}

        {/* Description - Hidden when any section is active */}
        {!isAnySectionActive && (
          <Stack
            tokens={{ childrenGap: 16 }}
            styles={{ root: { maxWidth: "600px" } }}
          >
            <Text
              variant="large"
              styles={{
                root: {
                  color: "#605e5c",
                  lineHeight: "1.6",
                },
              }}
            >
              Configure and manage inbound webhook endpoints to receive events
              from external systems.
            </Text>

            <Text
              variant="medium"
              styles={{
                root: {
                  color: "#8a8886",
                  fontStyle: "italic",
                },
              }}
            >
              Set up secure webhook receivers for real-time data processing and
              integration.
            </Text>
          </Stack>
        )}

        {/* Features Preview */}
        <Stack
          horizontal
          tokens={{ childrenGap: 40 }}
          styles={{
            root: {
              marginTop: "32px",
              "@media (max-width: 768px)": {
                flexDirection: "column",
                gap: "16px",
              },
            },
          }}
        >
          <Stack
            tokens={{ childrenGap: 8 }}
            styles={{
              root: {
                alignItems: "center",
                cursor: "pointer",
                padding: "8px",
                borderRadius: "4px",
                ":hover": {
                  backgroundColor: "#f3f2f1",
                },
              },
            }}
            onClick={handleProviderClick}
          >
            <Icon
              iconName="WebAppBuilderFragment"
              styles={{ root: { fontSize: 32, color: "#0078d4" } }}
            />
            <Text
              variant="medium"
              styles={{ root: { fontWeight: "600", color: "#323130" } }}
            >
              Provider
            </Text>
          </Stack>

          <Stack
            tokens={{ childrenGap: 8 }}
            styles={{
              root: {
                alignItems: "center",
                cursor: "pointer",
                padding: "8px",
                borderRadius: "4px",
                ":hover": {
                  backgroundColor: "#f3f2f1",
                },
              },
            }}
            onClick={handleEventProcessingClick}
            className={
              isEventProcessingPolling && isEventProcessingActive
                ? "event-processing-fade"
                : ""
            }
          >
            <Icon
              iconName="CloudDownload"
              styles={{ root: { fontSize: 32, color: "#0078d4" } }}
            />
            <Text
              variant="medium"
              styles={{ root: { fontWeight: "600", color: "#323130" } }}
            >
              Event Processing
              <span
                style={{
                  minWidth: "24px",
                  display: "inline-block",
                  textAlign: "left",
                }}
              >
                {isEventProcessingPolling && isEventProcessingActive
                  ? ".".repeat(dotCount)
                  : ""}
              </span>
            </Text>
          </Stack>
        </Stack>
      </Stack>

      {/* Bottom Section - Shown when any section is active */}
      {isAnySectionActive && (
        <Stack
          styles={{
            root: {
              height: "85%",
              backgroundColor: "#faf9f8",
              borderTop: "1px solid #e1dfdd",
            },
          }}
        >
          {bothSectionsActive ? (
            // Both sections active - resizable split horizontally
            <div
              style={{ height: "100%", display: "flex", position: "relative" }}
            >
              {/* Provider Section */}
              <Stack
                styles={{
                  root: {
                    width: `${providerWidth}%`,
                    padding: "24px",
                    overflow: "hidden",
                  },
                }}
              >
                <Stack
                  horizontal
                  horizontalAlign="space-between"
                  verticalAlign="center"
                  styles={{ root: { marginBottom: "16px" } }}
                >
                  <Stack tokens={{ childrenGap: 8 }}>
                    <Text
                      variant="large"
                      styles={{ root: { fontWeight: "600", color: "#323130" } }}
                    >
                      Provider Configuration
                    </Text>
                    <Text
                      variant="medium"
                      styles={{ root: { color: "#605e5c" } }}
                    >
                      Configure webhook providers and their settings.
                    </Text>
                  </Stack>

                  <Icon
                    iconName="Cancel"
                    styles={{
                      root: {
                        fontSize: 20,
                        color: "#605e5c",
                        cursor: "pointer",
                        padding: "8px",
                        borderRadius: "4px",
                        ":hover": {
                          backgroundColor: "#e1dfdd",
                          color: "#323130",
                        },
                      },
                    }}
                    onClick={handleCloseProvider}
                  />
                </Stack>

                <ProviderConfiguration
                  providers={providers}
                  isLoading={isLoadingProviders}
                  error={error}
                  onDelete={handleDeleteProvider}
                  onEdit={handleEditProvider}
                  onAdd={handleAddProvider}
                  onRefresh={loadProviders}
                />
              </Stack>

              {/* Resizable Splitter */}
              <div
                onMouseDown={handleMouseDown}
                style={{
                  width: "8px",
                  backgroundColor: "#e1dfdd",
                  cursor: "col-resize",
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  userSelect: "none",
                }}
              >
                <div
                  style={{
                    width: "2px",
                    height: "40px",
                    backgroundColor: "#8a8886",
                    borderRadius: "1px",
                  }}
                />
              </div>

              {/* Event Processing Section */}
              <Stack
                styles={{
                  root: {
                    width: `${100 - providerWidth}%`,
                    padding: "24px",
                    overflow: "hidden",
                  },
                }}
              >
                <Stack
                  horizontal
                  horizontalAlign="space-between"
                  verticalAlign="center"
                  styles={{ root: { marginBottom: "16px" } }}
                >
                  <Stack tokens={{ childrenGap: 8 }}>
                    <Text
                      variant="large"
                      styles={{ root: { fontWeight: "600", color: "#323130" } }}
                    >
                      Event Processing
                    </Text>
                    <Text
                      variant="medium"
                      styles={{ root: { color: "#605e5c" } }}
                    >
                      Monitor and process webhook events in real-time.
                    </Text>
                  </Stack>

                  <Icon
                    iconName="Cancel"
                    styles={{
                      root: {
                        fontSize: 20,
                        color: "#605e5c",
                        cursor: "pointer",
                        padding: "8px",
                        borderRadius: "4px",
                        ":hover": {
                          backgroundColor: "#e1dfdd",
                          color: "#323130",
                        },
                      },
                    }}
                    onClick={handleCloseEventProcessing}
                  />
                </Stack>

                <EventProcessing
                  providers={providers}
                  onRefresh={loadProviders}
                  isVisible={isEventProcessingActive}
                  onPollingStateChange={handleEventProcessingPollingChange}
                />
              </Stack>
            </div>
          ) : (
            // Single section active - full width
            <Stack styles={{ root: { height: "100%", padding: "24px" } }}>
              {isProviderViewActive && (
                <>
                  <Stack
                    horizontal
                    horizontalAlign="space-between"
                    verticalAlign="center"
                    styles={{ root: { marginBottom: "16px" } }}
                  >
                    <Stack tokens={{ childrenGap: 8 }}>
                      <Text
                        variant="large"
                        styles={{
                          root: { fontWeight: "600", color: "#323130" },
                        }}
                      >
                        Provider Configuration
                      </Text>
                      <Text
                        variant="medium"
                        styles={{ root: { color: "#605e5c" } }}
                      >
                        Configure webhook providers and their settings.
                      </Text>
                    </Stack>

                    <Icon
                      iconName="Cancel"
                      styles={{
                        root: {
                          fontSize: 20,
                          color: "#605e5c",
                          cursor: "pointer",
                          padding: "8px",
                          borderRadius: "4px",
                          ":hover": {
                            backgroundColor: "#e1dfdd",
                            color: "#323130",
                          },
                        },
                      }}
                      onClick={handleCloseProvider}
                    />
                  </Stack>

                  <ProviderConfiguration
                    providers={providers}
                    isLoading={isLoadingProviders}
                    error={error}
                    onDelete={handleDeleteProvider}
                    onEdit={handleEditProvider}
                    onAdd={handleAddProvider}
                    onRefresh={loadProviders}
                  />
                </>
              )}

              {isEventProcessingActive && (
                <>
                  <Stack
                    horizontal
                    horizontalAlign="space-between"
                    verticalAlign="center"
                    styles={{ root: { marginBottom: "16px" } }}
                  >
                    <Stack tokens={{ childrenGap: 8 }}>
                      <Text
                        variant="large"
                        styles={{
                          root: { fontWeight: "600", color: "#323130" },
                        }}
                      >
                        Event Processing
                      </Text>
                      <Text
                        variant="medium"
                        styles={{ root: { color: "#605e5c" } }}
                      >
                        Monitor and process webhook events in real-time.
                      </Text>
                    </Stack>

                    <Icon
                      iconName="Cancel"
                      styles={{
                        root: {
                          fontSize: 20,
                          color: "#605e5c",
                          cursor: "pointer",
                          padding: "8px",
                          borderRadius: "4px",
                          ":hover": {
                            backgroundColor: "#e1dfdd",
                            color: "#323130",
                          },
                        },
                      }}
                      onClick={handleCloseEventProcessing}
                    />
                  </Stack>

                  <EventProcessing
                    providers={providers}
                    onRefresh={loadProviders}
                    isVisible={isEventProcessingActive}
                    onPollingStateChange={handleEventProcessingPollingChange}
                  />
                </>
              )}
            </Stack>
          )}
        </Stack>
      )}

      {/* Success Message Bar */}
      {successMessage && (
        <MessageBar
          messageBarType={MessageBarType.success}
          isMultiline={false}
          onDismiss={() => setSuccessMessage(null)}
          styles={{
            root: {
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              zIndex: 10001,
            },
          }}
        >
          {successMessage}
        </MessageBar>
      )}

      {/* Warning Messages Bar */}
      {warningMessages.length > 0 && (
        <MessageBar
          messageBarType={MessageBarType.warning}
          isMultiline={true}
          onDismiss={() => setWarningMessages([])}
          styles={{
            root: {
              position: "fixed",
              top: successMessage ? "48px" : 0,
              left: 0,
              width: "100%",
              zIndex: 10001,
            },
          }}
        >
          {warningMessages.map((msg, index) => (
            <div key={index}>{msg}</div>
          ))}
        </MessageBar>
      )}

      {/* Webhook Provider Panel */}
      <WebhookProviderPanel
        isPanelOpenned={isPanelOpen}
        mode={panelMode}
        existingProvider={
          selectedProvider
            ? {
                Id: selectedProvider.Id,
                InboundWebhookSecurityTypeId:
                  selectedProvider.InboundWebhookSecurityTypeId,
                InboundWebhookProviderTypeId:
                  selectedProvider.InboundWebhookProviderTypeId,
                Description: selectedProvider.Description,
                ProviderUrl: selectedProvider.ProviderUrl,
              }
            : undefined
        }
        onPanelClosed={() => setIsPanelOpen(false)}
        onSave={handleProviderSaved}
        onShowSuccess={handleShowSuccess}
        onShowWarnings={handleShowWarnings}
      />
    </Stack>
  );
};

export default WebhooksInboundPage;
